import { useCallback, useEffect, useRef, useState } from 'react';
import { VIDEO_PACK, serializeTags, type VideoPackField } from '../lib/videoPack.ts';
import { scoreMedia, type ScorableMedia } from '../lib/mediaHealth.ts';
import { metaFieldPatch, rowFieldPatch, type DetailMediaItem, type SavePatch } from '../lib/mediaFieldPatch.ts';

/**
 * Visual CMS — the SPREADSHEET view (visual-cms.md §4 / §7 Slice 3). A tab-across-rows inline-
 * edit grid over the SAME filtered set the gallery shows, for fast metadata entry: columns =
 * title · category · description · tags · transcript · alt. Empty key fields are highlighted
 * amber (the SAME `scoreMedia` health model as the cards). Save-as-you-type (debounced) via
 * the SAME `media-update` call as the modal (through the shared patch builders — merges). On a
 * live client every cell is disabled (read-only; media-update 403s live regardless).
 *
 * Reads/writes ONLY an item's own fields — never grouping/assign/cover/order/export (invariant).
 */

const DEBOUNCE_MS = 600;

// The editable columns, left→right (the native tab order flows across each row then down).
const PACK_BY_KEY: Record<string, VideoPackField> = Object.fromEntries(VIDEO_PACK.map((f) => [f.key, f]));
type Col = { key: 'title' | 'category' | 'description' | 'tags' | 'transcript' | 'alt'; label: string; wide?: boolean; row?: boolean };
const COLS: Col[] = [
  { key: 'title', label: 'Title' },
  { key: 'category', label: 'Category' },
  { key: 'description', label: 'Description', wide: true },
  { key: 'tags', label: 'Tags' },
  { key: 'transcript', label: 'Transcript', wide: true },
  { key: 'alt', label: 'Alt text', row: true },
];

function seedFor(item: DetailMediaItem, col: Col): string {
  if (col.row) return item.alt ?? '';
  if (col.key === 'tags') return serializeTags(item.metadata?.tags);
  return (item.metadata?.[col.key] as string | undefined) ?? '';
}

export function MediaSpreadsheet({
  items,
  live,
  onSave,
}: {
  items: DetailMediaItem[];
  live: boolean;
  onSave: (id: string, patch: SavePatch) => Promise<void>;
}) {
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((t) => clearTimeout(t));
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, []);

  // Debounced per-cell save (keyed id:field so edits to different cells don't cancel). Builds
  // the patch via the shared builders → identical write to the modal's.
  const save = useCallback(
    (cellId: string, patch: SavePatch, id: string) => {
      if (live) return;
      const existing = timers.current.get(cellId);
      if (existing) clearTimeout(existing);
      setSaveState('saving');
      timers.current.set(
        cellId,
        setTimeout(() => {
          void onSave(id, patch)
            .then(() => {
              setSaveState('saved');
              if (savedTimer.current) clearTimeout(savedTimer.current);
              savedTimer.current = setTimeout(() => setSaveState('idle'), 1600);
            })
            .catch(() => setSaveState('idle'));
        }, DEBOUNCE_MS),
      );
    },
    [live, onSave],
  );

  function onCell(item: DetailMediaItem, col: Col, raw: string) {
    const patch = col.row ? rowFieldPatch('alt', raw) : metaFieldPatch(item, PACK_BY_KEY[col.key], raw);
    save(`${item.id}:${col.key}`, patch, item.id);
  }

  if (items.length === 0) {
    return <p className="rounded border border-line border-dashed bg-paper p-3 text-sm text-ink-2">No media matches.</p>;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <p className="text-xs text-ink-2">{items.length} item{items.length === 1 ? '' : 's'} · tab across to edit · changes save automatically</p>
        <span className="ml-auto text-xs text-success" aria-live="polite">
          {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? '✓ Saved' : ''}
        </span>
      </div>
      <div className="overflow-x-auto rounded border border-line">
        <table className="w-full min-w-[56rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-line bg-paper-2 text-left text-xs uppercase tracking-wide text-ink-2">
              <th className="p-2 font-semibold">Media</th>
              {COLS.map((c) => (
                <th key={c.key} className="p-2 font-semibold">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((m) => {
              const missing = new Set<string>(scoreMedia(m as ScorableMedia).missing.map((x) => x.key));
              const fname = (m.original_filename ?? '').split('/').pop() ?? '';
              const thumb = m.kind === 'video' ? m.poster : m.url;
              return (
                <tr key={m.id} className="border-b border-line align-top last:border-0">
                  {/* row identity — thumbnail + filename (non-editable) */}
                  <td className="w-40 p-2">
                    <div className="flex items-center gap-2">
                      <span className="relative grid h-9 w-14 flex-none place-items-center overflow-hidden rounded bg-paper-2">
                        {thumb ? <img src={thumb} alt="" className="h-full w-full object-cover" /> : <span className="text-[9px] text-ink-2">—</span>}
                        {m.kind === 'video' && (
                          <span className="pointer-events-none absolute grid h-5 w-5 place-items-center rounded-full bg-black/40 text-white" aria-hidden="true">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                          </span>
                        )}
                      </span>
                      <span className="min-w-0 truncate font-mono text-[10px] text-ink-2" title={fname}>{fname || m.kind}</span>
                    </div>
                  </td>
                  {COLS.map((c) => {
                    const flagged = missing.has(c.key);
                    const cls = `w-full rounded border px-1.5 py-1 text-xs ${flagged ? 'border-warning-bright bg-warning-bright/5' : 'border-line bg-paper'} ${live ? 'opacity-60' : ''}`;
                    return (
                      <td key={c.key} className={c.wide ? 'min-w-[16rem] p-2' : 'min-w-[9rem] p-2'}>
                        {c.wide ? (
                          <textarea
                            defaultValue={seedFor(m, c)}
                            disabled={live}
                            rows={2}
                            onChange={(e) => onCell(m, c, e.target.value)}
                            className={cls}
                          />
                        ) : (
                          <input
                            defaultValue={seedFor(m, c)}
                            disabled={live}
                            onChange={(e) => onCell(m, c, e.target.value)}
                            className={cls}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
